import { Check } from '@mui/icons-material';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Input } from '@on3/ui-lib/components/Input/Input';
import { useClickOutside } from '@on3/ui-lib/hooks/useClickOutside';
import { handleKeyDown } from '@on3/ui-lib/utils/accessibility/keyDown';
import clsx from 'clsx';
import { useRef, useState } from 'react';

import styles from './SelectSearch.module.scss';

type SelectSearchOption<T = object> = {
  key: number;
  label: string;
  avatarUrl?: string | null;
} & T;

interface SelectSearchProps<T> {
  label?: string;
  options:
    | SelectSearchOption<T>[]
    | { group: string; options: SelectSearchOption<T>[] }[];
  onChange: (option: SelectSearchOption<T>) => void;
  value?: SelectSearchOption<T> | null;
}

export const SelectSearch = <T extends object>({
  label,
  options,
  onChange,
  value = null,
}: SelectSearchProps<T>) => {
  const [selected, setSelected] = useState<SelectSearchOption<T> | null>(value);
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useClickOutside(dropdownRef, () => {
    setIsOpen(false);
    setFocusedIndex(-1);
  });

  // Flatten all options for consistent indexing
  const getFlattenedOptions = () => {
    if ('group' in options[0]) {
      return (options as { group: string; options: SelectSearchOption<T>[] }[])
        .map((group) =>
          group.options
            .filter((option) =>
              option.label.toLowerCase().includes(search.toLowerCase()),
            )
            .map((option, index) => ({
              ...option,
              flatIndex: index,
            })),
        )
        .flat();
    }

    return (options as SelectSearchOption<T>[])
      .filter((option) =>
        option.label.toLowerCase().includes(search.toLowerCase()),
      )
      .map((option, index) => ({
        ...option,
        flatIndex: index,
      }));
  };

  const flattenedOptions = getFlattenedOptions();
  const availableOptions =
    'group' in options[0]
      ? (options as { group: string; options: SelectSearchOption<T>[] }[])
          .map((group) => ({
            group: group.group,
            options: group.options.filter((option) =>
              option.label.toLowerCase().includes(search.toLowerCase()),
            ),
          }))
          .filter((group) => group.options.length > 0)
      : (options as SelectSearchOption<T>[]).filter((option) =>
          option.label.toLowerCase().includes(search.toLowerCase()),
        );

  const handleToggle = () => {
    setIsOpen(!isOpen);
    setFocusedIndex(-1);
  };

  const handleSelect = (option: SelectSearchOption<T>) => {
    setSelected(option);
    setIsOpen(false);
    setFocusedIndex(-1);
    setSearch('');
    onChange(option);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && focusedIndex >= 0) {
      e.preventDefault();
      const option = flattenedOptions[focusedIndex];

      if (option) {
        handleSelect(option);
      }
    }

    if (e.key === 'Escape') {
      setIsOpen(false);
      setFocusedIndex(-1);
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex((prev) =>
        prev < flattenedOptions.length - 1 ? prev + 1 : 0,
      );
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex((prev) =>
        prev > 0 ? prev - 1 : flattenedOptions.length - 1,
      );
    }
  };

  // Get flat index for a specific option
  const getFlatIndex = (option: SelectSearchOption<T>) => {
    return flattenedOptions.findIndex((opt) => opt.key === option.key);
  };

  return (
    <div className={styles.container} ref={dropdownRef}>
      <button
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className={styles.selectButton}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        type="button"
      >
        {selected?.avatarUrl && (
          <Avatar
            className={styles.selectedAvi}
            source={selected.avatarUrl}
            teamAvatar
          />
        )}
        <span className={styles.selectedText}>
          {selected?.label || label || 'Select an option'}
        </span>
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownSearch}>
            <Input
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              label="Search"
              onChange={(e) => setSearch(e.target.value)}
              placeholder={label || 'Search...'}
              srLabel
              value={search}
            />
          </div>
          <ul className={styles.optionsList} role="listbox">
            {availableOptions.map((item, index) =>
              'group' in item ? (
                <li className={styles.optionGroup} key={item.group}>
                  <span className={styles.optionGroupLabel}>{item.group}</span>
                  <ul>
                    {item.options.map((groupOption) => {
                      const flatIndex = getFlatIndex(groupOption);

                      return (
                        <li
                          aria-selected={selected?.key === groupOption.key}
                          className={clsx(styles.option, {
                            [styles.focused]: focusedIndex === flatIndex,
                            [styles.selected]:
                              selected?.key === groupOption.key,
                          })}
                          key={groupOption.key}
                          onClick={() => handleSelect(groupOption)}
                          onFocus={() => setFocusedIndex(flatIndex)}
                          onKeyDown={onKeyDown}
                          onMouseEnter={() => setFocusedIndex(flatIndex)}
                          role="option"
                          tabIndex={0}
                        >
                          {selected?.key === groupOption.key && (
                            <span className={styles.checkIcon}>
                              <Check aria-hidden="true" />
                            </span>
                          )}
                          <span className={styles.optionText}>
                            {groupOption.label}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ) : (
                <li
                  aria-selected={selected?.key === item.key}
                  className={clsx(styles.option, {
                    [styles.focused]: focusedIndex === index,
                    [styles.selected]: selected?.key === item.key,
                  })}
                  key={item.key}
                  onClick={() => handleSelect(item)}
                  onFocus={() => setFocusedIndex(index)}
                  onKeyDown={onKeyDown}
                  onMouseEnter={() => setFocusedIndex(index)}
                  role="option"
                  tabIndex={0}
                >
                  {selected?.key === item.key && (
                    <span className={styles.checkIcon}>
                      <Check aria-hidden="true" />
                    </span>
                  )}
                  <span className={styles.optionText}>{item.label}</span>
                </li>
              ),
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
